import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { integrationService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ScrollText, Loader2 } from "lucide-react";
import { useTenantScope, TenantPicker, SelectTenantNotice, PageHeader, Badge, RUN_STYLE, fmtDate } from "./shared";

export default function SyncLogsPage() {
  const [sp] = useSearchParams();
  const jobId = sp.get("jobId") || "";
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setItems([]); return; }
    setLoading(true);
    integrationService.syncLogs({ ...params, ...(jobId ? { syncJobId: jobId } : {}), size: 100 })
      .then((d) => setItems(d.items || []))
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [blocked, params, jobId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="sync-logs-page">
      <PageHeader icon={ScrollText} title="Sync Logs" subtitle="Every sync run with record-level outcomes.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="logs-empty"><p className="text-sm text-muted-foreground">No sync runs yet.</p></div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="logs-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-muted-foreground border-b border-border">
                <th className="px-4 py-2">Started</th><th className="px-4 py-2">Target</th><th className="px-4 py-2">Trigger</th>
                <th className="px-4 py-2 text-center">Read</th><th className="px-4 py-2 text-center">Created</th>
                <th className="px-4 py-2 text-center">Updated</th><th className="px-4 py-2 text-center">Failed</th><th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-b border-border last:border-0" data-testid={`log-row-${l.id}`}>
                  <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">{fmtDate(l.startedAt)}</td>
                  <td className="px-4 py-2 text-foreground">{l.targetEntity}</td>
                  <td className="px-4 py-2 text-muted-foreground capitalize">{l.trigger}</td>
                  <td className="px-4 py-2 text-center">{l.counts?.read || 0}</td>
                  <td className="px-4 py-2 text-center text-emerald-600">{l.counts?.created || 0}</td>
                  <td className="px-4 py-2 text-center text-sky-600">{l.counts?.updated || 0}</td>
                  <td className="px-4 py-2 text-center text-red-600">{l.counts?.failed || 0}</td>
                  <td className="px-4 py-2"><Badge label={l.status} styleMap={RUN_STYLE} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
